import { Component, OnInit, OnDestroy, Inject, ViewChild, ElementRef, NgZone } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { ModalController } from '@ionic/angular';
import { catchError, firstValueFrom, of, Subscription } from 'rxjs';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ActiveToast, ToastrService } from 'ngx-toastr';

import { SessionsService } from '../../services/sessions/sessions.service';
import { DolbyService } from '../../services/dolby/dolby.service';
import { StillHerePage } from '../../modals/still-here/still-here.page';
import { UserService } from '../../services/user/user.service';
import { WindowToken } from '../../services/window/window';
import { Locations, ParticipantFsDaily, ParticipantFsDolby, Prompt, Roles, StudioSidebarItem } from '@sc/types';
import { StudioSidebarService } from '../../services/studio-sidebar/studio-sidebar.service';
import { ShowsService } from '../../services/shows/shows.service';
import { PromptsService } from '../../services/prompts/prompts.service';
import { PromptPage } from '../../modals/prompt/prompt.page';
import { EquipmentService } from '../../services/equipment/equipment.service';
import { WalletService } from '../../services/wallet/wallet.service';
import { Wallet } from '@sc/types';
import { Plan } from '@sc/types';
import { GeneralToastComponent } from '../../toasts/general-toast/general-toast.component';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { AuthService } from '../../services/auth/auth.service';
import { RecordingService } from '../../services/recording/recording.service';
import { SettingsService } from '../../services/settings/settings.service';
import { OrganizationsService } from '../../services/organizations/organizations.service';
import { SquadshotService } from '../../services/squadshot/squadshot.service';
import { ResponsiveService } from '../../services/responsive/responsive.service';
import { RolesService } from '../../services/roles/roles.service';
import { setCssVar } from '../../util/setCssVar';
import { DailyService } from '../../services/daily/daily.service';

@Component({
  selector: 'sc-studio',
  templateUrl: 'studio.page.html',
  styleUrls: ['studio.page.scss'],
})
export class StudioPage implements OnInit, OnDestroy {
  @ViewChild('canvasContainer') canvasContainer: ElementRef;
  user$ = this.userService.activeUser$;
  session$ = this.sessionsService.studioSession$;
  participant$ = this.sessionsService.participant$;
  prompts$ = this.sessionsService.sessionPrompts$;
  sidebarItems$ = this.studioSidebarService.sidebarItems$;
  plan$ = this.walletService.studioPlan$;
  wallet$ = this.walletService.studioWallet$;
  squadshot$ = this.squadshotService.squadshot$;
  recording$ = this.recordingService.recording$;

  dolbyConversation$ = this.dolbyService.dolbyConversation$;
  dolbyPrioritySpeakersArray$ = this.dolbyService.prioritySpeakersArray$;
  dolbyPrioritySpeakersArrayObjectFit$ = this.dolbyService.prioritySpeakersArrayObjectFit$;
  dolbyNonPrioritySpeakersArray$ = this.dolbyService.nonPrioritySpeakersArray$;
  dolbyNonPrioritySpeakersArrayObjectFit$ = this.dolbyService.nonPrioritySpeakersArrayObjectFit$;
  dolbyLeaving$ = this.dolbyService.leaving$;

  dailyCall$ = this.dailyService.dailyCall$;
  dailyPrioritySpeakersArray$ = this.dailyService.prioritySpeakersArray$;
  dailyPrioritySpeakersArrayObjectFit$ = this.dailyService.prioritySpeakersArrayObjectFit$;
  dailyNonPrioritySpeakersArray$ = this.dailyService.nonPrioritySpeakersArray$;
  dailyNonPrioritySpeakersArrayObjectFit$ = this.dailyService.nonPrioritySpeakersArrayObjectFit$;
  dailyLeaving$ = this.dailyService.leaving$;

  recordingCountdown: number;
  recordingCountdownInterval: NodeJS.Timer;
  leftSidebars: StudioSidebarItem[];
  rightSidebars: StudioSidebarItem[];
  requestPermissionOpen = true;
  greenRoomOpen = false;
  renderBackstage = false;
  canvasHorizontalLayout = false;
  canvasMutationObserver: MutationObserver;
  isMobileDevice;
  roles = Roles;
  guestEmail: string;
  invalidStudio = false;

  subs: Array<Subscription> = [];

  constructor(
    @Inject(WindowToken) private window: Window,
    private analyticsService: AnalyticsService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private toastrService: ToastrService,
    private modalController: ModalController,
    private equipmentService: EquipmentService,
    private userService: UserService,
    private sessionsService: SessionsService,
    private showsService: ShowsService,
    private deviceDetectorService: DeviceDetectorService,
    private idle: Idle,
    private dolbyService: DolbyService,
    private squadshotService: SquadshotService,
    private studioSidebarService: StudioSidebarService,
    private promptsService: PromptsService,
    private settingsService: SettingsService,
    private walletService: WalletService,
    private recordingService: RecordingService,
    private organizationsService: OrganizationsService,
    private responsiveService: ResponsiveService,
    private rolesService: RolesService,
    private dailyService: DailyService,
    private elm: ElementRef
  ) {}

  /**
   * Sets up the initial component state
   */
  async ngOnInit() {
    if (this.session$.value?.confType !== 'daily' || this.dolbyConversation$.value) {
      this.dolbyService.leaving$.next(false);
    }

    if (this.session$.value?.confType === 'daily' || this.dailyCall$.value) {
      this.dailyService.leaving$.next(false);
    }

    this.equipmentService.joinedStudio();
    this.isMobileDevice = this.deviceDetectorService.isMobile();
    setCssVar(this.elm.nativeElement, 'canvas-min-width', this.studioSidebarService.canvasMinWidth + 'px');
    this.checkMigration();
    this.setupRouteParams();
    this.setupPrompts();
    this.setupConversation();
    this.setupFrozen();
    this.setupPaused();
    this.setupRecordingCountdown();
    this.setupClosed();
    this.setupSidebarItems();
  }

  async checkMigration() {
    const auth = await this.userService.authUser$.toPromise();
    if (auth && !auth.isAnonymous) {
      setTimeout(async () => {
        if (!this.userService.userData$.value) {
          this.authService.setupUser(auth);
        }
      }, 2000);
    }
  }

  setupConversation() {
    const dolbySub = this.dolbyConversation$.subscribe((conversation) => {
      if (!conversation) return;
      this.setupIdle();
      this.initSidebarToggle();
      this.setupHorizontalCanvasLayout();
    });
    this.subs.push(dolbySub);

    const dailySub = this.dailyCall$.subscribe((call) => {
      if (!call) return;
      this.setupIdle();
      this.initSidebarToggle();
      this.setupHorizontalCanvasLayout();
    });
    this.subs.push(dailySub);
  }

  async initSidebarToggle() {
    await this.responsiveService.constraints$.nextExistingValue();
    if (this.responsiveService.constraints$.value.width >= 768) {
      if (this.rolesService.role$.value >= Roles.SHOW_MANAGER) {
        this.studioSidebarService.toggleSidebar('participants', true);
      } else {
        this.studioSidebarService.toggleSidebar('chat', true);
      }
    }
  }

  /**
   * Asks, "Are you still here?" Netflix style
   */
  setupIdle() {
    this.idle.setIdle(60); // 1 minute
    this.idle.setTimeout(7200); // 2 hours in seconds
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    const sub = this.idle.onTimeout.subscribe(() => {
      this.modalController
        .create({
          component: StillHerePage,
          componentProps: {},
          showBackdrop: true,
          backdropDismiss: false,
          animated: true,
          cssClass: 'still-here-modal',
        })
        .then((modal) => {
          modal.present();
        });
    });
    this.subs.push(sub);

    this.idle.watch();
  }

  /**
   * Sets up the active show and session or redirects based on the URL
   */
  async setupRouteParams() {
    const sub = this.router.events.subscribe(async (event) => {
      if (!(event instanceof NavigationEnd)) return;
      if (event.url.includes('/studio/')) {
        if (this.route.snapshot.data) {
          await this.user$.toPromise();
          if (this.user$.value.guest) this.analyticsService.init(this.user$.value.uid);
          const session = this.route.snapshot.data.session;
          this.guestEmail = this.route.snapshot.data.session.guestEmail;
          if (session.location === 'backstage') {
            if (this.guestEmail) {
              //FIXME: this needs to do something?
              // console.log(`📸 Checking ${this.guestEmail} for backstage access... 📷 `);
            }
            this.renderBackstage = true;
          }

          const validSession = await this.sessionsService.checkSession(session.sessionID);
          if (validSession) this.sessionsService.setStudioSession(session.sessionID);
          let validShow = await this.showsService.checkShow(session.showID);

          // Checks if the URL is an OLD Url from v4. If so, redirect to the new URL
          // Start by verifing that we have vaild ShowID from the SessionID params
          // Then check that the HOSTID is a valid User. If both are true then just replace the URL
          // With the correct formated SHOWID/SESSIONID.
          if (!validShow && validSession) {
            const foundSession = await firstValueFrom(this.sessionsService.getSessionByID(session.sessionID));
            const showID = foundSession.showID;
            const isValidUser = await firstValueFrom(this.userService.getUserData(session.showID));
            validShow = await this.showsService.checkShow(showID);
            if (validShow && isValidUser) {
              if (this.guestEmail)
                this.sessionsService.setParticipantSearchInfo(this.user$.value.uid, session.sessionID, {
                  email: this.guestEmail,
                });

              const link = `/studio/${showID}/session/${session.sessionID}`;
              this.window.location.href = link;
            } else {
              this.invalidStudio = true;
              this.router.navigate(['404'], { replaceUrl: true });
            }
          } else {
            if (validSession && validShow) {
              if (this.guestEmail)
                this.sessionsService.setParticipantSearchInfo(this.user$.value.uid, session.sessionID, {
                  email: this.guestEmail,
                });
            } else {
              this.invalidStudio = true;
              this.router.navigate(['404'], { replaceUrl: true });
            }
          }
        }
      } else {
        this.ngOnDestroy();
      }
    });

    this.subs.push(sub);
  }

  setupSidebarItems() {
    const sub = this.sidebarItems$.subscribe((items: StudioSidebarItem[]) => {
      this.leftSidebars = items.slice(0, Math.floor(items.length / 2));
      this.rightSidebars = items.slice(Math.floor(items.length / 2), items.length);
    });

    this.subs.push(sub);
  }

  /**
   * Closes the request permission UI
   *
   * @param status
   */
  closeRequestPermission(status: { hasPermission: boolean[] }) {
    if (status.hasPermission[0]) {
      setTimeout(() => {
        // Timeout is fix for expression changed error
        this.requestPermissionOpen = false;
        this.greenRoomOpen = true;
      }, 0);
    }
  }

  trackByParticipantDolby(index: number, participant: ParticipantFsDolby) {
    return (participant as ParticipantFsDolby).uid;
  }

  trackByParticipantDaily(index: number, participant: ParticipantFsDaily) {
    return (participant as ParticipantFsDaily).userData.scUID;
  }

  trackBySidebar(index: number) {
    return index;
  }

  /**
   * Setups up Prompts to display toast messages for an generated prompt for this session.
   */
  setupPrompts() {
    const sub = this.prompts$.subscribe((prompts: Prompt[]) => {
      prompts.forEach(async (prompt: Prompt) => {
        try {
          if (this.promptsService.activePromptIDs.has(prompt.promptID)) return;
          this.promptsService.activePromptIDs.add(prompt.promptID);
          // only prompt the targets
          if (
            (prompt.targetParticipants && prompt.targetParticipants.includes(this.user$.value.uid)) ||
            (prompt.targetGroup && prompt.targetGroup === Locations.EVERYONE) ||
            (prompt.targetRole && this.participant$.value?.role > prompt.targetRole)
          ) {
            if (prompt.presentAs === 'toast') {
              const toast: ActiveToast<GeneralToastComponent> = this.toastrService.info(prompt.message, prompt.title, {
                closeButton: true,
                tapToDismiss: false,
                toastComponent: GeneralToastComponent,
              });
              toast.toastRef.componentInstance.prompt = prompt;
              await firstValueFrom(
                toast.onShown.pipe(
                  catchError(() => {
                    console.error('toast.onShown completed without emitting any items');
                    return of(null); // Provide a default value or handle the error
                  })
                )
              );
              await this.sessionsService.deletePrompt(prompt);
            }

            if (prompt.presentAs === 'modal') {
              const modal = await this.modalController.create({
                component: PromptPage,
                componentProps: { prompt },
                showBackdrop: true,
                backdropDismiss: true,
                animated: true,
                cssClass: 'prompt-modal',
              });

              await modal.present();
              await modal.onDidDismiss();
              await this.sessionsService.deletePrompt(prompt);
            }

            if (prompt.presentAs === 'silent') {
              this.promptsService.getPromptActions(prompt.type).map((action) => action.handler(prompt));
            }
          }
        } catch (error) {
          console.log('Error setting up prompt', error);
        }
      });
    });
    this.subs.push(sub);
  }

  /**
   * Redirects to the payment info page if account is frozen
   */
  setupFrozen() {
    const sub = this.wallet$.subscribe((wallet: Wallet) => {
      if (!wallet) return;
      if (wallet.frozen) {
        const protocol = this.window.location.protocol;
        const hostName = this.window.location.host;
        this.window.location.href = `${protocol}//${hostName}/account/billing`;
      }
    });
    this.subs.push(sub);
  }

  /**
   * Redirects to the dashboard page if account is paused
   */
  setupPaused() {
    const sub = this.plan$.subscribe((plan: Plan) => {
      if (!plan) return;
      if (plan.name === 'Pause') {
        const protocol = this.window.location.protocol;
        const hostName = this.window.location.host;
        this.window.location.href = `${protocol}//${hostName}/dashboard`;
      }
    });
    this.subs.push(sub);
  }

  async setupClosed() {
    await this.session$.nextExistingValue();
    const closed = !(await this.organizationsService.orgHasSubscription(this.session$.value.orgID));

    if (closed) {
      this.invalidStudio = true;
      this.router.navigate(['404'], { replaceUrl: true });
    }
  }

  setupRecordingCountdown() {
    const sub = this.recordingService.startRecordingCountdown$.subscribe((start) => {
      if (start && this.settingsService.studioShowSettings$.value?.recordingCountdown) {
        this.recordingCountdown = this.settingsService.studioShowSettings$.value.recordingCountdown;
        this.recordingCountdownInterval = setInterval(() => {
          this.recordingCountdown--;
          if (this.recordingCountdown <= 0) {
            clearInterval(this.recordingCountdownInterval);
            this.recordingCountdown = 0;
          }
        }, 1000);
      } else this.recordingCountdown = 0;
    });

    this.subs.push(sub);
  }

  async setupHorizontalCanvasLayout() {
    // wait for next tick so the element is rendered
    await new Promise((resolve) => setTimeout(resolve, 0));
    this.canvasMutationObserver?.disconnect();
    if (!this.canvasContainer) return;

    this.canvasMutationObserver = new MutationObserver(async (mutation) => {
      // wait for next tick to avoid observer reacting to itself
      await new Promise((resolve) => setTimeout(resolve, 0));
      this.canvasHorizontalLayout = this.canvasContainer?.nativeElement?.clientWidth > 1700;
    });

    this.canvasMutationObserver.observe(this.canvasContainer.nativeElement, {
      attributes: true,
      attributeFilter: ['style'],
      subtree: true,
    });
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => sub.unsubscribe());
    if (this.canvasMutationObserver) this.canvasMutationObserver.disconnect();
    this.equipmentService.leftStudio();
    this.sessionsService.setStudioSession(null);
    if (!this.invalidStudio) {
      if (this.dolbyConversation$.value) {
        if (!this.dolbyService.leaving$?.value) this.dolbyService.leaveConversation(!this.greenRoomOpen);
        else this.dolbyService.leaving$.next(false);
      }

      if (this.dailyCall$.value) {
        if (!this.dailyService.leaving$?.value) this.dailyService.leaveCall(!this.greenRoomOpen);
        else this.dailyService.leaving$.next(false);
      }
    }
  }
}
